import { Grid, IconButton, Typography, makeStyles } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from "@material-ui/lab";
import { ERowMovementTypes } from "api/enums";
import { IRowMovement } from "api/interfaces";
import clsx from "clsx";
import AttachmentsMoviment from "components/Attachments/AttachmentsMoviment";
import LabelField from "components/shared/Contract/LabelField";
import { lang } from "i18nLang";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import {
  ContractRowMovementStore,
  ContractRowStore,
  ContractStore,
  MovimentsAttachmentStore,
} from "store";
import { formatNumber, formatNumberToMonetary } from "utils";

const useStyles = makeStyles((theme) => ({
  title: {
    fontWeight: 700,
    color: "#AFCBFF",
  },
  headerContent: {
    padding: "0.5rem",
    height: "fit-content",
    backgroundColor: "#2E2E2E",
  },
  contentScroll: {
    height: "calc(100vh - 86px) !important",
    overflow: "scroll",
  },
  content: {
    padding: "0.5rem",
    height: "100%",
  },

  timeline: { padding: "6px 0", width: "100%" },
  timelineItem: {
    "&::before": {
      content: "unset",
    },
    "& .dot": {
      padding: "6px",
      marginBottom: 0,
    },
    "& .colorPrimary": {
      backgroundColor: theme.palette.primary.main,
    },
    "& .title": {
      color: "white",
      fontSize: "0.875rem",
      fontWeight: 500,
    },
    "& .date": {
      color: "white",
      fontSize: "0.688rem",
    },
  },
  timelineContent: {
    width: "100%",
  },
}));

const MenuLineHistory = observer(() => {
  const classes = useStyles();
  const row = ContractRowStore.getSelectedRow();
  const [movements, setMovements] = useState<IRowMovement[]>([]);
  const [showAttachments, setShowAttachments] = useState(false);

  useEffect(() => {
    ContractRowMovementStore.getRowsMovements(row).then((movement) => {
      if (movement) {
        const datas = Object.values(movement.data).reverse();
        getAttachments(datas);
        setMovements(datas);
      }
    });
  }, [row]);

  function getAttachments(movements: IRowMovement[]) {
    setShowAttachments(false);
    MovimentsAttachmentStore.fetchMovimentsAttachments(movements).finally(
      () => {
        setShowAttachments(true);
      }
    );
  }

  const handleClickClose = () => {
    ContractStore.closeLeftSideMenu();
  };

  const renderDetails = (mov: IRowMovement) => {
    if (mov.type === ERowMovementTypes.READJUSTMENT) {
      let indicator = mov.indicator || "";
      if (indicator === "manual") {
        indicator = lang("lineHistory.indicator.manual");
      }

      return (
        <>
          <LabelField caption={lang("fields.indicator")} value={indicator} />
          <LabelField
            caption={lang("lineHistory.indicator.percentual")}
            value={`${formatNumber(mov.indicatorPercentual || 0)}%`}
          />
          <LabelField
            caption={lang("lineHistory.indicator.total")}
            value={formatNumberToMonetary(mov.totalContract || 0)}
          />
          <LabelField
            caption={lang("lineHistory.indicator.balance")}
            value={formatNumberToMonetary(mov.balanceContract || 0)}
          />
        </>
      );
    }

    if (mov.type === ERowMovementTypes.EDIT_DESCRIPTION) {
      return (
        <>
          <LabelField
            caption={lang("lineHistory.description")}
            value={mov.description || ""}
          />
          <LabelField
            caption={lang("lineHistory.oldDescription")}
            value={mov.oldDescription || ""}
          />
        </>
      );
    }

    return (
      <>
        {mov.type !== ERowMovementTypes.CONTRACT && (
          <LabelField
            caption={lang(`lineHistory.qtts.${mov.type}`)}
            value={formatNumber(mov.quantityApplied || mov.quantity || 0, 2, 5)}
          />
        )}
        {mov.type !== ERowMovementTypes.UPDATED && (
          <LabelField
            caption={lang("fields.quantity")}
            value={formatNumber(mov.quantity || 0, 2, 5)}
          />
        )}
        <LabelField
          caption={lang("fields.total")}
          value={formatNumberToMonetary(mov.total || 0)}
        />
        <LabelField
          caption={lang("lineHistory.totalContract")}
          value={formatNumberToMonetary(mov.totalContract || 0)}
        />
      </>
    );
  };

  return (
    <>
      <Grid
        container
        item
        className={classes.headerContent}
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="body2" className={classes.title}>
          {lang("lineHistory.title")}
        </Typography>
        <IconButton onClick={handleClickClose} style={{ padding: 0 }}>
          <CloseIcon color="secondary" />
        </IconButton>
      </Grid>
      <Grid
        container
        item
        className={clsx(classes.content, classes.contentScroll)}
        justifyContent="flex-start"
        alignContent="flex-start"
      >
        <Grid style={{ width: "100%" }}>
          <LabelField
            caption={lang("fields.code")}
            value={row?.structured || ""}
          />
          <LabelField
            caption={lang("fields.description")}
            value={row?.description || ""}
          />
        </Grid>
        <Timeline className={classes.timeline}>
          {movements.map((mov, index) => {
            const isEven = index % 2 === 0;
            const date =
              mov.type === ERowMovementTypes.READJUSTMENT
                ? mov.indicatorDate?.toLocaleDateString("pt-br") || "-"
                : mov.date.toLocaleDateString("pt-br");
            const userLabel = mov.user ? `- ${mov.user.name}` : "";

            return (
              <TimelineItem className={classes.timelineItem} key={index}>
                <TimelineSeparator>
                  <TimelineDot
                    className={`dot ${isEven ? "colorPrimary" : ""}`}
                  />
                  <TimelineConnector className={isEven ? "colorPrimary" : ""} />
                </TimelineSeparator>
                <TimelineContent className={classes.timelineContent}>
                  <Typography className="title">
                    {lang(`lineHistory.types.${mov.type}`)}
                  </Typography>
                  <Typography className="date">{`${date} ${userLabel}`}</Typography>
                  {renderDetails(mov)}
                  {showAttachments && (
                    <AttachmentsMoviment
                      index={index}
                      applyChanges={true}
                      cardColumnSize={9}
                      selectedRow={row}
                    />
                  )}
                </TimelineContent>
              </TimelineItem>
            );
          })}
        </Timeline>
      </Grid>
    </>
  );
});

export default MenuLineHistory;
